import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  fetchActivity,
  type ActivityResponse,
  type AgentActivity,
  type DecidedApproval,
  type Goal,
  type Grant,
  type PendingApproval,
  type Schedule,
} from '../api/activity';
import { useAuth } from '../auth/AuthContext';
import {
  absoluteTime,
  approvalCommands,
  argPreview,
  classifyFailure,
  decidedBy,
  goalCommands,
  grantIds,
  isLapsed,
  pendingIds,
  plural,
  relativeTime,
  revokeCommand,
  scheduleLastRun,
  scheduleWhen,
  uniquePrefix,
  waitingCount,
  type ActivityFailure,
} from '../components/agenda/agendaHelpers';
import './AgendaPage.css';

/**
 * Each agent's standing work: what waits on the owner, the goals it is
 * pursuing, the grants that let it act without asking, the schedules it
 * set itself, and what was recently decided.
 *
 * **Not the Activity page.** `/activity` is what the runtime *did* —
 * sessions and audit events. This is what is still open. The route it
 * reads happens to be `/api/activity`, which is the core's name for it.
 *
 * **Read-only, on purpose.** Every action here is a chat command, printed
 * to be copied and sent to the agent it names — the same `/approvals` and
 * `/goals` an owner already uses, answered by the agent that owns the
 * record. The page is a view of that state, not a second way to change it.
 *
 * **An unavailable section says why.** Goals switched off for an agent,
 * or a store that could not be read, arrive as `available: false` with a
 * reason, and render as that reason — never as an empty list, which would
 * read as "nothing to do".
 *
 * Refreshes every 15 s, as Overview and Procedures do. Every relative time
 * is measured from the server's `now_ms`, not the browser's clock.
 */

const REFRESH_MS = 15_000;

export function AgendaPage() {
  const { user } = useAuth();
  const [answer, setAnswer] = useState<ActivityResponse | null>(null);
  const [failure, setFailure] = useState<ActivityFailure | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Requests can overlap on a slow link. Only the newest one may write,
  // so an old answer cannot replace a newer one.
  const latest = useRef(0);

  const load = useCallback(() => {
    latest.current += 1;
    const ticket = latest.current;
    fetchActivity().then(
      (resp) => {
        if (ticket !== latest.current) return;
        setAnswer(resp);
        setFailure(null);
        setLoaded(true);
      },
      (err: unknown) => {
        if (ticket !== latest.current) return;
        setFailure(classifyFailure(err));
        setLoaded(true);
      },
    );
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <AgendaView
      answer={answer}
      failure={failure}
      loaded={loaded}
      role={user?.role ?? null}
    />
  );
}

/**
 * The page for one state of the fetch, with no context or effects of its
 * own — so every state can be rendered from sample data.
 */
export function AgendaView({
  answer,
  failure,
  loaded,
  role,
}: {
  answer: ActivityResponse | null;
  failure: ActivityFailure | null;
  loaded: boolean;
  role: string | null;
}) {
  const waiting = answer ? waitingCount(answer) : 0;
  return (
    <div className="agenda-page">
      <header className="agenda-page__header">
        <div>
          <h1 className="agenda-page__title">Agenda</h1>
          <p className="agenda-page__note">
            What each agent is waiting on you for, working on, and has
            scheduled. Nothing here acts: each action is a chat command to
            copy, sent to the agent it is listed under.
          </p>
        </div>
        {answer && (
          <span className="agenda-dim">
            {waiting > 0 ? (
              <span className="agenda-page__alarm">
                {waiting} waiting on you ·{' '}
              </span>
            ) : null}
            <span title={absoluteTime(answer.now_ms)}>
              as of {new Date(answer.now_ms).toLocaleTimeString()}
            </span>
            {' · '}refreshes every {REFRESH_MS / 1000}s
          </span>
        )}
      </header>

      {failure && (
        <FailureNotice failure={failure} role={role} stale={answer !== null} />
      )}

      {!loaded ? (
        <p className="agenda-dim">Loading…</p>
      ) : answer === null ? null : answer.agents.length === 0 ? (
        <p className="agenda-dim">This instance reports no agents.</p>
      ) : (
        answer.agents.map((agent) => (
          <AgentAgenda
            key={agent.agent_id}
            agent={agent}
            nowMs={answer.now_ms}
            approvalIds={pendingIds(answer)}
            grantIdList={grantIds(answer)}
          />
        ))
      )}
    </div>
  );
}

function FailureNotice({
  failure,
  role,
  stale,
}: {
  failure: ActivityFailure;
  role: string | null;
  stale: boolean;
}) {
  const text =
    failure.kind === 'missing'
      ? "This panel's core does not serve /api/activity. The Agenda needs machinai 0.62.0 or later, and this instance runs an older one."
      : failure.kind === 'forbidden'
        ? `The Agenda needs the editor role${
            role ? `; you are signed in as ${role}` : ''
          }.`
        : `The Agenda could not be read: ${failure.message}`;
  return (
    <div className="agenda-error" role="alert">
      {text}
      {stale && (
        <div className="agenda-error__stale">
          What follows is the last answer that did arrive, and may be out of
          date.
        </div>
      )}
    </div>
  );
}

function AgentAgenda({
  agent,
  nowMs,
  approvalIds,
  grantIdList,
}: {
  agent: AgentActivity;
  nowMs: number;
  approvalIds: string[];
  grantIdList: string[];
}) {
  const { approvals, goals, grants, schedules } = agent;
  return (
    <section className="agenda-agent" aria-label={agent.agent_id}>
      <header className="agenda-agent__header">
        <h2 className="agenda-agent__name">{agent.agent_id}</h2>
        <span className="agenda-dim">
          send its commands in a chat with {agent.agent_id}
        </span>
      </header>

      <Section
        title="Waiting on you"
        count={approvals.available ? approvals.pending.length : null}
        available={approvals.available}
        reason={approvals.reason}
        empty={approvals.pending.length === 0}
        emptyText="Nothing is waiting on you."
        alarm
      >
        <ul className="agenda-list">
          {approvals.pending.map((a) => (
            <PendingCard
              key={a.id}
              approval={a}
              prefix={uniquePrefix(a.id, approvalIds)}
              nowMs={nowMs}
            />
          ))}
        </ul>
      </Section>

      <Section
        title="Goals"
        count={goals.available ? goals.open.length : null}
        available={goals.available}
        reason={goals.reason}
        empty={goals.open.length === 0 && goals.ended.length === 0}
        emptyText="No open goals, and none ended in the last 7 days."
      >
        {goals.open.length === 0 ? (
          <p className="agenda-dim">No open goals.</p>
        ) : (
          <ul className="agenda-list">
            {goals.open.map((g) => (
              <GoalCard key={g.id} goal={g} nowMs={nowMs} />
            ))}
          </ul>
        )}
        {goals.ended.length > 0 && (
          <details className="agenda-ended">
            <summary>
              Ended in the last 7 days ({goals.ended.length})
            </summary>
            <ul className="agenda-rows">
              {goals.ended.map((g) => (
                <EndedGoalRow key={g.id} goal={g} nowMs={nowMs} />
              ))}
            </ul>
          </details>
        )}
      </Section>

      <Section
        title="Standing grants"
        count={grants.available ? grants.live.length : null}
        available={grants.available}
        reason={grants.reason}
        empty={grants.live.length === 0}
        emptyText="No standing grants are live."
      >
        <ul className="agenda-list">
          {grants.live.map((g) => (
            <GrantCard
              key={g.id}
              grant={g}
              prefix={uniquePrefix(g.id, grantIdList)}
              nowMs={nowMs}
            />
          ))}
        </ul>
      </Section>

      <Section
        title="Schedules"
        count={schedules.available ? schedules.entries.length : null}
        available={schedules.available}
        reason={schedules.reason}
        empty={schedules.entries.length === 0}
        emptyText="The agent has set no schedules of its own."
      >
        <ul className="agenda-rows">
          {schedules.entries.map((s) => (
            <ScheduleRow key={s.id} schedule={s} />
          ))}
        </ul>
      </Section>

      <Section
        title="Recently decided"
        count={null}
        available={approvals.available}
        reason={approvals.reason}
        empty={approvals.decided.length === 0}
        emptyText="No decided approvals."
      >
        <ul className="agenda-rows">
          {approvals.decided.map((d) => (
            <DecidedRow key={d.id} decided={d} nowMs={nowMs} />
          ))}
        </ul>
      </Section>
    </section>
  );
}

/**
 * One section: its reason when unavailable, its empty text when there is
 * nothing, and its rows otherwise. The three are kept apart because two
 * of them look identical as an empty list.
 */
function Section({
  title,
  count,
  available,
  reason,
  empty,
  emptyText,
  alarm,
  children,
}: {
  title: string;
  count: number | null;
  available: boolean;
  reason: string | null;
  empty: boolean;
  emptyText: string;
  alarm?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="agenda-section">
      <h3 className="agenda-section__title">
        {title}
        {count !== null && count > 0 && (
          <span
            className={`agenda-count${alarm ? ' agenda-count--alarm' : ''}`}
          >
            {count}
          </span>
        )}
      </h3>
      {!available ? (
        <p className="agenda-unavailable">
          Not available: {reason ?? 'the core gave no reason.'}
        </p>
      ) : empty ? (
        <p className="agenda-dim">{emptyText}</p>
      ) : (
        children
      )}
    </div>
  );
}

function When({
  ms,
  nowMs,
  prefix,
}: {
  ms: number | null;
  nowMs: number;
  prefix?: string;
}) {
  return (
    <span className="agenda-when" title={absoluteTime(ms)}>
      {prefix ? `${prefix} ` : ''}
      {ms === null ? 'unknown' : relativeTime(ms, nowMs)}
    </span>
  );
}

function PendingCard({
  approval,
  prefix,
  nowMs,
}: {
  approval: PendingApproval;
  prefix: string;
  nowMs: number;
}) {
  const lapsed = isLapsed(approval, nowMs);
  return (
    <li className={`agenda-card agenda-card--waiting${lapsed ? ' is-lapsed' : ''}`}>
      <div className="agenda-card__top">
        <span className="agenda-card__tool">{approval.tool_name}</span>
        {approval.grantable ? (
          <span
            className="agenda-badge agenda-badge--grantable"
            title="approve <id> session covers later calls of this tool, asked by the same gates, in this session for at most 24 h; approve <id> hour covers them across the agent for an hour (ADR 062)"
          >
            can be granted for a session or an hour
          </span>
        ) : (
          <span
            className="agenda-badge"
            title="A gate that asked this one refuses standing grants, so it is answered once only (ADR 062 D4)"
          >
            answered once only
          </span>
        )}
        <span className="agenda-card__times">
          <When ms={approval.created_ms} nowMs={nowMs} prefix="asked" />
          {' · '}
          {lapsed ? (
            <span className="agenda-lapsed">
              <When ms={approval.expires_ms} nowMs={nowMs} prefix="lapsed" />
            </span>
          ) : (
            <When ms={approval.expires_ms} nowMs={nowMs} prefix="expires" />
          )}
        </span>
      </div>
      {approval.prompt && <p className="agenda-card__prompt">{approval.prompt}</p>}
      <div className="agenda-card__meta">
        {approval.gates.length > 0 && (
          <span className="agenda-chips">
            {approval.gates.map((gate) => (
              <span key={gate} className="agenda-chip">
                {gate}
              </span>
            ))}
          </span>
        )}
        <span className="agenda-dim">
          {approval.message_source === 'user'
            ? 'asked in conversation'
            : `asked during a ${approval.message_source} turn`}
          {approval.channel_id ? ` · ${approval.channel_id}` : ''}
          {approval.session_id ? ` · session ${approval.session_id.slice(0, 12)}` : ''}
        </span>
      </div>
      <details className="agenda-args">
        <summary>
          <span className="agenda-args__label">arguments</span>{' '}
          <code>{argPreview(approval.tool_args)}</code>
        </summary>
        <pre>{JSON.stringify(approval.tool_args, null, 2)}</pre>
      </details>
      {lapsed && (
        <p className="agenda-dim">
          It lapsed unanswered and can no longer be approved; deny records
          that and unblocks the session.
        </p>
      )}
      <Commands commands={approvalCommands(approval, prefix, nowMs)} />
    </li>
  );
}

function GoalCard({ goal, nowMs }: { goal: Goal; nowMs: number }) {
  const overdue = goal.deadline_ms <= nowMs;
  return (
    <li className={`agenda-card agenda-card--goal is-${goal.status}`}>
      <div className="agenda-card__top">
        <span className={`agenda-badge agenda-badge--${goal.status}`}>
          {goal.status}
        </span>
        {goal.may_write && (
          <span
            className="agenda-badge agenda-badge--write"
            title="The owner let this goal's turns change things"
          >
            may write
          </span>
        )}
        <span className="agenda-card__times">
          {goal.turns_used}/{plural(goal.max_turns, 'turn')}
          {' · '}
          <When
            ms={goal.deadline_ms}
            nowMs={nowMs}
            prefix={overdue ? 'deadline passed' : 'deadline'}
          />
          {goal.status === 'active' && (
            <>
              {' · '}
              {goal.next_run_ms <= nowMs ? (
                <span className="agenda-when" title={absoluteTime(goal.next_run_ms)}>
                  next turn due
                </span>
              ) : (
                <When ms={goal.next_run_ms} nowMs={nowMs} prefix="next turn" />
              )}
            </>
          )}
        </span>
      </div>
      <p className="agenda-card__objective">{goal.objective}</p>
      {goal.last_report ? (
        <p className="agenda-card__report">
          <span className="agenda-chip">{goal.last_report}</span>{' '}
          {goal.last_summary}{' '}
          <When ms={goal.updated_ms} nowMs={nowMs} />
        </p>
      ) : (
        <p className="agenda-dim">
          No report yet · set <When ms={goal.created_ms} nowMs={nowMs} />
        </p>
      )}
      <Commands commands={goalCommands(goal)} />
    </li>
  );
}

function EndedGoalRow({ goal, nowMs }: { goal: Goal; nowMs: number }) {
  return (
    <li className="agenda-row">
      <span className={`agenda-badge agenda-badge--${goal.status}`}>
        {goal.status}
      </span>
      <span className="agenda-row__main">
        {goal.objective}
        {goal.last_summary && (
          <span className="agenda-dim"> — {goal.last_summary}</span>
        )}
      </span>
      <span className="agenda-dim">
        {goal.turns_used}/{plural(goal.max_turns, 'turn')} ·{' '}
        <When ms={goal.updated_ms} nowMs={nowMs} prefix="ended" />
      </span>
    </li>
  );
}

function GrantCard({
  grant,
  prefix,
  nowMs,
}: {
  grant: Grant;
  prefix: string;
  nowMs: number;
}) {
  return (
    <li className="agenda-card agenda-card--grant">
      <div className="agenda-card__top">
        <span className="agenda-card__tool">{grant.tool_name}</span>
        <span className="agenda-badge">
          {grant.scope === 'session'
            ? `session ${(grant.session_id ?? '').slice(0, 12) || '(unrecorded)'}`
            : grant.scope === 'agent'
              ? 'the whole agent'
              : grant.scope}
        </span>
        <span className="agenda-card__times">
          <When ms={grant.expires_ms} nowMs={nowMs} prefix="expires" />
        </span>
      </div>
      <div className="agenda-card__meta">
        {grant.gates.length > 0 && (
          <span className="agenda-chips">
            {grant.gates.map((gate) => (
              <span key={gate} className="agenda-chip">
                {gate}
              </span>
            ))}
          </span>
        )}
        <span className="agenda-dim">
          used {grant.uses === 1 ? 'once' : `${grant.uses} times`} · from
          approval {grant.approval_id.slice(0, 8)} by{' '}
          {grant.created_by || 'unattributed'} ·{' '}
          <When ms={grant.created_ms} nowMs={nowMs} prefix="granted" />
        </span>
      </div>
      <Commands commands={[revokeCommand(prefix)]} />
    </li>
  );
}

function ScheduleRow({ schedule }: { schedule: Schedule }) {
  return (
    <li className={`agenda-row${schedule.enabled ? '' : ' is-disabled'}`}>
      <span className={`agenda-badge agenda-badge--${schedule.kind}`}>
        {schedule.kind}
      </span>
      <span className="agenda-row__main">{schedule.name}</span>
      <span className="agenda-dim">
        {scheduleWhen(schedule)} · {scheduleLastRun(schedule)}
        {schedule.enabled ? '' : ' · disabled'}
      </span>
    </li>
  );
}

function DecidedRow({
  decided,
  nowMs,
}: {
  decided: DecidedApproval;
  nowMs: number;
}) {
  const by = decidedBy(decided);
  return (
    <li className="agenda-row">
      <span className={`agenda-badge agenda-badge--${decided.state}`}>
        {decided.state}
      </span>
      <span className="agenda-row__main">{decided.tool_name}</span>
      <span className="agenda-dim">
        by {by.label}
        {by.grantId ? ` ${by.grantId.slice(0, 8)}` : ''} ·{' '}
        <When ms={decided.resolved_ms} nowMs={nowMs} />
        {decided.resolution_reason ? ` · ${decided.resolution_reason}` : ''}
      </span>
    </li>
  );
}

function Commands({ commands }: { commands: string[] }) {
  if (commands.length === 0) return null;
  return (
    <div className="agenda-commands">
      {commands.map((c) => (
        <CopyCommand key={c} text={c} />
      ))}
    </div>
  );
}

/**
 * A command, selectable in one click and copyable with the button beside
 * it. The button writes to the clipboard and nothing else. Where the
 * browser refuses (no secure context, or permission denied) it says so,
 * and the text is still selectable.
 */
function CopyCommand({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  const copy = () => {
    if (!navigator.clipboard) {
      setState('failed');
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => setState('copied'),
      () => setState('failed'),
    );
  };

  return (
    <span className="agenda-cmd">
      <code className="agenda-cmd__text">{text}</code>
      <button type="button" className="agenda-cmd__copy" onClick={copy}>
        {state === 'copied'
          ? 'copied'
          : state === 'failed'
            ? 'select and copy'
            : 'copy'}
      </button>
    </span>
  );
}
